import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  MapPin,
  Loader2,
  Pencil,
  CreditCard,
  ShoppingCart,
  Receipt,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TradingCustomerModal } from '@/components/trading/customers/customer-modal';
import { TradingCustomerAnalyticsTab } from '@/components/trading/customers/analytics-tab';
import { ContactsCard } from '@/components/trading/customers/contacts-card';
import { CreditLimitDialog } from '@/components/trading/customers/credit-limit-dialog';
import { trpc } from '@/lib/trpc';
import { formatCurrency, formatDate, cn } from '@/lib/utils';

export function TradingCustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const canEdit = usePermission('CUSTOMERS', 'EDIT', 'TRADING');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);

  const { data: customer, isLoading, error } = trpc.tradingCustomer.byId.useQuery(
    id!,
    { enabled: !!id }
  );

  const { data: creditStatus } = trpc.tradingCustomer.getCreditStatus.useQuery(
    id!,
    { enabled: !!id && !!customer?.creditLimitEnabled }
  );

  const { data: ordersData } = trpc.tradingCustomer.getOrders.useQuery(
    { customerId: id!, page: 1, pageSize: 5 },
    { enabled: !!id }
  );

  const { data: invoicesData } = trpc.tradingCustomer.getInvoices.useQuery(
    { customerId: id!, page: 1, pageSize: 5 },
    { enabled: !!id }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{tCommon('loading')}</span>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/trading/customers')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('customers.backToList')}
        </Button>
        <div className="border border-red-300 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{tCommon('error')}</p>
          <p className="text-sm">{error?.message || t('customers.notFound')}</p>
        </div>
      </div>
    );
  }

  const customerTypeLabels: Record<string, string> = {
    BUSINESS: t('customers.types.business'),
    DISTRIBUTOR: t('customers.types.distributor'),
    RETAILER: t('customers.types.retailer'),
    GOVERNMENT: t('customers.types.government'),
    NONPROFIT: t('customers.types.nonprofit'),
  };

  const paymentTermsLabels: Record<string, string> = {
    PREPAID: t('customers.paymentTermsOptions.prepaid'),
    CREDIT_15: t('customers.paymentTermsOptions.net15'),
    CREDIT_30: t('customers.paymentTermsOptions.net30'),
    CREDIT_60: t('customers.paymentTermsOptions.net60'),
    CREDIT_90: t('customers.paymentTermsOptions.net90'),
  };

  const creditLimitValue =
    typeof customer.creditLimit === 'object'
      ? customer.creditLimit.toNumber()
      : customer.creditLimit;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/trading/customers')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('customers.backToList')}
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center bg-blue-100">
              <Building2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{customer.companyName}</h1>
              {customer.tradeName && (
                <p className="text-muted-foreground">{customer.tradeName}</p>
              )}
            </div>
          </div>
        </div>
        {canEdit && (
          <Button onClick={() => setIsModalOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            {tCommon('edit')}
          </Button>
        )}
      </div>

      <TradingCustomerModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        customer={customer}
      />

      <CreditLimitDialog
        open={creditDialogOpen}
        onOpenChange={setCreditDialogOpen}
        customerId={customer.id}
        customerName={customer.companyName}
        currentCreditLimitEnabled={customer.creditLimitEnabled}
        currentCreditLimit={creditLimitValue}
        currentCreditCurrency={customer.creditCurrency}
      />

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">{t('customers.tabs.overview')}</TabsTrigger>
          <TabsTrigger value="contacts">{t('customers.tabs.contacts')}</TabsTrigger>
          <TabsTrigger value="orders">{t('customers.tabs.orders')}</TabsTrigger>
          <TabsTrigger value="invoices">{t('customers.tabs.invoices')}</TabsTrigger>
          <TabsTrigger value="analytics">{t('customers.tabs.analytics')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Status Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('customers.status')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('customers.statusLabel')}</span>
                  <span
                    className={cn(
                      'px-2 py-0.5 text-xs font-medium',
                      customer.isActive
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    )}
                  >
                    {customer.isActive ? tCommon('active') : tCommon('inactive')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('customers.type')}</span>
                  <span className="font-medium">
                    {customerTypeLabels[customer.customerType]}
                  </span>
                </div>
                {customer.industry && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('customers.industry')}</span>
                    <span className="font-medium">{customer.industry}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Primary Contact Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('customers.primaryContact')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="font-medium">{customer.contactName}</div>
                {customer.contactTitle && (
                  <div className="text-sm text-muted-foreground">
                    {customer.contactTitle}
                  </div>
                )}
                {customer.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a
                      href={`mailto:${customer.email}`}
                      className="text-primary hover:underline"
                    >
                      {customer.email}
                    </a>
                  </div>
                )}
                {customer.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a
                      href={`tel:${customer.phone}`}
                      className="hover:underline"
                    >
                      {customer.phone}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Credit Status Card */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">{t('customers.credit.title')}</CardTitle>
                {canEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCreditDialogOpen(true)}
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    {t('customers.credit.manage')}
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {customer.creditLimitEnabled ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        {t('customers.credit.creditLimit')}
                      </span>
                      <span className="font-medium">
                        {formatCurrency(creditLimitValue, customer.creditCurrency)}
                      </span>
                    </div>
                    {creditStatus && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">
                            {t('customers.credit.outstandingBalance')}
                          </span>
                          <span className="font-medium">
                            {formatCurrency(
                              creditStatus.outstandingBalance,
                              customer.creditCurrency
                            )}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">
                            {t('customers.credit.availableCredit')}
                          </span>
                          <span
                            className={cn(
                              'font-medium',
                              creditStatus.isOverLimit && 'text-red-600'
                            )}
                          >
                            {formatCurrency(
                              creditStatus.availableCredit ?? 0,
                              customer.creditCurrency
                            )}
                          </span>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('customers.credit.noCreditLimit')}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {/* Address Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t('customers.primaryAddress')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {customer.addressLine1 ||
                customer.city ||
                customer.country ? (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <div className="text-sm">
                      {customer.addressLine1 && <div>{customer.addressLine1}</div>}
                      {customer.addressLine2 && <div>{customer.addressLine2}</div>}
                      <div>
                        {[customer.city, customer.state, customer.postalCode]
                          .filter(Boolean)
                          .join(', ')}
                      </div>
                      {customer.country && <div>{customer.country}</div>}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('customers.noAddress')}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Billing Address Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t('customers.billingAddress')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {customer.billingAddressLine1 ||
                customer.billingCity ||
                customer.billingCountry ? (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <div className="text-sm">
                      {customer.billingAddressLine1 && (
                        <div>{customer.billingAddressLine1}</div>
                      )}
                      {customer.billingAddressLine2 && (
                        <div>{customer.billingAddressLine2}</div>
                      )}
                      <div>
                        {[
                          customer.billingCity,
                          customer.billingState,
                          customer.billingPostalCode,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </div>
                      {customer.billingCountry && (
                        <div>{customer.billingCountry}</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('customers.sameAsPrimary')}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Financial Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('customers.financialInfo')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <span className="text-sm text-muted-foreground">
                    {t('customers.paymentTerms')}
                  </span>
                  <p className="font-medium">
                    {paymentTermsLabels[customer.paymentTerms] || customer.paymentTerms}
                  </p>
                </div>
                {customer.taxId && (
                  <div>
                    <span className="text-sm text-muted-foreground">
                      {t('customers.taxId')}
                    </span>
                    <p className="font-medium">{customer.taxId}</p>
                  </div>
                )}
                <div>
                  <span className="text-sm text-muted-foreground">
                    {t('customers.taxExempt')}
                  </span>
                  <p className="font-medium">
                    {customer.taxExempt ? tCommon('yes') : tCommon('no')}
                  </p>
                  {customer.taxExempt && customer.taxExemptReason && (
                    <p className="text-sm text-muted-foreground">
                      {customer.taxExemptReason}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {(customer.notes || customer.internalNotes) && (
            <div className="grid gap-4 md:grid-cols-2">
              {customer.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{t('customers.notes')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm whitespace-pre-wrap">{customer.notes}</p>
                  </CardContent>
                </Card>
              )}
              {customer.internalNotes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      {t('customers.internalNotes')}
                    </CardTitle>
                    <CardDescription>
                      {t('customers.internalNotesHint')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm whitespace-pre-wrap">
                      {customer.internalNotes}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="contacts">
          <ContactsCard customerId={customer.id} contacts={customer.contacts || []} />
        </TabsContent>

        <TabsContent value="orders" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('customers.recentOrders')}</CardTitle>
                <CardDescription>
                  {ordersData?.pagination.total || 0} {t('customers.totalOrders')}
                </CardDescription>
              </div>
              <Button variant="outline" asChild>
                <Link to={`/trading/sales/orders?customerId=${customer.id}`}>
                  {t('customers.viewAllOrders')}
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {ordersData?.orders.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('customers.noOrders')}
                </p>
              ) : (
                <div className="space-y-4">
                  {ordersData?.orders.map((order) => (
                    <Link
                      key={order.id}
                      to={`/trading/sales/orders/${order.id}`}
                      className="flex items-center justify-between p-3 border border-border hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{order.orderNumber}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(order.orderDate)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">
                          {formatCurrency(
                            typeof order.total === 'object'
                              ? order.total.toNumber()
                              : order.total,
                            order.currency
                          )}
                        </p>
                        <span
                          className={cn(
                            'text-xs px-2 py-0.5 inline-block',
                            order.status === 'DELIVERED'
                              ? 'bg-green-100 text-green-700'
                              : order.status === 'CANCELLED'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-blue-100 text-blue-700'
                          )}
                        >
                          {order.status}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('customers.recentInvoices')}</CardTitle>
                <CardDescription>
                  {invoicesData?.pagination.total || 0} {t('customers.totalInvoices')}
                </CardDescription>
              </div>
              <Button variant="outline" asChild>
                <Link to={`/trading/finance/invoices?customerId=${customer.id}`}>
                  {t('customers.viewAllInvoices')}
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {invoicesData?.invoices.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('customers.noInvoices')}
                </p>
              ) : (
                <div className="space-y-4">
                  {invoicesData?.invoices.map((invoice) => (
                    <Link
                      key={invoice.id}
                      to={`/trading/finance/invoices/${invoice.id}`}
                      className="flex items-center justify-between p-3 border border-border hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <Receipt className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{invoice.invoiceNumber}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(invoice.invoiceDate)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">
                          {formatCurrency(
                            typeof invoice.total === 'object'
                              ? invoice.total.toNumber()
                              : invoice.total,
                            invoice.currency
                          )}
                        </p>
                        <div className="flex items-center gap-2">
                          {Number(invoice.balanceDue) > 0 && (
                            <span className="text-xs text-orange-600">
                              {t('customers.due')}:{' '}
                              {formatCurrency(
                                Number(invoice.balanceDue),
                                invoice.currency
                              )}
                            </span>
                          )}
                          <span
                            className={cn(
                              'text-xs px-2 py-0.5',
                              invoice.status === 'PAID'
                                ? 'bg-green-100 text-green-700'
                                : invoice.status === 'CANCELLED'
                                ? 'bg-red-100 text-red-700'
                                : invoice.status === 'OVERDUE'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-blue-100 text-blue-700'
                            )}
                          >
                            {invoice.status}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics">
          <TradingCustomerAnalyticsTab customerId={id!} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
